import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const ProfessionalAdventuresManager = () => {
  const [adventures, setAdventures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedAdventure, setSelectedAdventure] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  // Form state for adventure
  const [formData, setFormData] = useState(getEmptyFormData());

  function getEmptyFormData() {
    return {
      slug: '',
      title: '',
      tagline: '',
      short_description: '',
      full_description: '',
      hero_image: '',
      hero_image_alt: '',
      gallery_images: [],
      base_price: 0,
      currency: 'AUD',
      price_display: '',
      duration: '',
      group_size_min: 1,
      group_size_max: 8,
      skill_level: 'beginner',
      category: 'scuba-diving',
      tags: [],
      location: '',
      meeting_point: '',
      what_to_bring: [],
      included_items: [],
      excluded_items: [],
      highlights: [],
      itinerary: [],
      is_active: true,
      is_featured: false,
      requires_certification: false,
      min_age: 12,
      max_age: null,
      fitness_requirements: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      social_image: '',
      booking_enabled: true,
      advance_booking_days: 1,
      cancellation_policy: '',
      terms_conditions: '',
      sort_order: 0
    };
  }

  const skillLevels = [
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' },
    { value: 'all-levels', label: 'All Levels' }
  ];

  const categories = [
    { value: 'scuba-diving', label: 'Scuba Diving' },
    { value: 'snorkeling', label: 'Snorkeling' },
    { value: 'camping', label: 'Camping' },
    { value: 'combined', label: 'Combined Adventure' }
  ];

  useEffect(() => {
    fetchAdventures();
  }, []);

  const fetchAdventures = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/adventures`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAdventures(data);
      } else {
        setError('Failed to fetch adventures');
      }
    } catch (err) {
      setError('Failed to load adventures: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const saveAdventure = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('adminToken');
      
      // Prepare the data
      const saveData = {
        ...formData,
        base_price: parseFloat(formData.base_price) || 0,
        group_size_min: parseInt(formData.group_size_min) || 1,
        group_size_max: parseInt(formData.group_size_max) || 8,
        min_age: parseInt(formData.min_age) || 12,
        max_age: formData.max_age ? parseInt(formData.max_age) : null,
        advance_booking_days: parseInt(formData.advance_booking_days) || 1,
        sort_order: parseInt(formData.sort_order) || 0
      };

      let response;
      
      if (isCreating) {
        // Create new adventure
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/adventures`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(saveData)
        });
      } else {
        // Update existing adventure
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/adventures/${selectedAdventure.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(saveData)
        });
      }

      if (response.ok) {
        const savedAdventure = await response.json();
        setSuccess(`Adventure "${savedAdventure.title}" saved successfully!`);
        
        // Refresh the adventures list
        await fetchAdventures();
        
        // Reset form if creating
        if (isCreating) {
          setFormData(getEmptyFormData());
          setIsCreating(false);
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
        
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to save adventure');
      }
    } catch (err) {
      setError('Failed to save adventure: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const editAdventure = (adventure) => {
    setSelectedAdventure(adventure);
    setFormData({
      ...adventure,
      tags: adventure.tags || [],
      gallery_images: adventure.gallery_images || [],
      what_to_bring: adventure.what_to_bring || [],
      included_items: adventure.included_items || [],
      excluded_items: adventure.excluded_items || [],
      highlights: adventure.highlights || [],
      itinerary: adventure.itinerary || []
    });
    setIsCreating(false);
    setActiveTab('basic');
  };

  const createNewAdventure = () => {
    setSelectedAdventure(null);
    setFormData(getEmptyFormData());
    setIsCreating(true);
    setActiveTab('basic');
  };

  const deleteAdventure = async (adventureId) => {
    if (!window.confirm('Are you sure you want to delete this adventure? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/adventures/${adventureId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('Adventure deleted successfully');
        await fetchAdventures();
        if (selectedAdventure?.id === adventureId) {
          setSelectedAdventure(null);
          setFormData(getEmptyFormData());
        }
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete adventure');
      }
    } catch (err) {
      setError('Failed to delete adventure: ' + err.message);
    }
  };

  const handleArrayFieldChange = (field, value) => {
    const array = value.split('\n').filter(item => item.trim() !== '');
    setFormData({...formData, [field]: array});
  };

  const handleTagsChange = (value) => {
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag !== '');
    setFormData({...formData, tags});
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: '📝' },
    { id: 'details', label: 'Details', icon: '📋' },
    { id: 'media', label: 'Media', icon: '📸' },
    { id: 'pricing', label: 'Pricing', icon: '💰' },
    { id: 'seo', label: 'SEO', icon: '🎯' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading adventures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Professional Adventures Manager</h2>
          <p className="text-gray-600 mt-1">Complete control over your diving adventures</p>
        </div>
        <div className="flex items-center space-x-4">
          {(isCreating || selectedAdventure) && (
            <button
              onClick={() => {
                setIsCreating(false);
                setSelectedAdventure(null);
                setFormData(getEmptyFormData());
              }}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={createNewAdventure}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <span className="mr-2">+</span>
            Add New Adventure
          </button>
        </div>
      </div>

      {/* Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700"
          >
            ❌ {error}
          </motion.div>
        )}
        
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700"
          >
            ✅ {success}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Adventures List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold mb-4">Your Adventures ({adventures.length})</h3>
            
            {adventures.length === 0 ? (
              <div className="text-center py-8">
                <span className="text-4xl mb-4 block">🤿</span>
                <p className="text-gray-500 mb-4">No adventures yet</p>
                <button
                  onClick={createNewAdventure}
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  Create your first adventure
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {adventures.map((adventure) => (
                  <motion.div
                    key={adventure.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedAdventure?.id === adventure.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => editAdventure(adventure)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 truncate">
                          {adventure.title}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {adventure.short_description}
                        </p>
                        <div className="flex items-center mt-2 text-xs text-gray-500">
                          <span className="bg-gray-100 px-2 py-1 rounded mr-2">
                            ${adventure.base_price}
                          </span>
                          <span className="bg-gray-100 px-2 py-1 rounded mr-2">
                            {adventure.category}
                          </span>
                          {adventure.is_featured && (
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end ml-4">
                        <div className={`w-3 h-3 rounded-full ${
                          adventure.is_active ? 'bg-green-500' : 'bg-red-500'
                        }`}></div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteAdventure(adventure.id);
                          }}
                          className="text-red-500 hover:text-red-700 text-xs mt-2"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Adventure Editor */}
        <div className="lg:col-span-2">
          {(isCreating || selectedAdventure) ? (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              {/* Tab Navigation */}
              <div className="border-b border-gray-200 px-6 pt-6">
                <div className="flex space-x-8">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <span className="mr-2">{tab.icon}</span>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Basic Info Tab */}
                {activeTab === 'basic' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Adventure Title *
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              title: e.target.value,
                              slug: generateSlug(e.target.value)
                            });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Great Barrier Reef Diving"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL Slug *
                        </label>
                        <input
                          type="text"
                          value={formData.slug}
                          onChange={(e) => setFormData({...formData, slug: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="great-barrier-reef-diving"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tagline
                      </label>
                      <input
                        type="text"
                        value={formData.tagline}
                        onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Explore the world's most beautiful coral reef"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Short Description *
                      </label>
                      <textarea
                        value={formData.short_description}
                        onChange={(e) => setFormData({...formData, short_description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="Brief description for adventure cards..."
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Description *
                      </label>
                      <textarea
                        value={formData.full_description}
                        onChange={(e) => setFormData({...formData, full_description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="6"
                        placeholder="Detailed description of the adventure..."
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({...formData, category: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>
                              {cat.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Skill Level
                        </label>
                        <select
                          value={formData.skill_level}
                          onChange={(e) => setFormData({...formData, skill_level: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {skillLevels.map(level => (
                            <option key={level.value} value={level.value}>
                              {level.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Details Tab */}
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duration
                        </label>
                        <input
                          type="text"
                          value={formData.duration}
                          onChange={(e) => setFormData({...formData, duration: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="3 days, 2 nights"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Min Group Size
                        </label>
                        <input
                          type="number"
                          value={formData.group_size_min}
                          onChange={(e) => setFormData({...formData, group_size_min: parseInt(e.target.value) || 1})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Max Group Size
                        </label>
                        <input
                          type="number"
                          value={formData.group_size_max}
                          onChange={(e) => setFormData({...formData, group_size_max: parseInt(e.target.value) || 8})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="1"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Great Barrier Reef, Australia"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meeting Point
                      </label>
                      <input
                        type="text"
                        value={formData.meeting_point}
                        onChange={(e) => setFormData({...formData, meeting_point: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Marina entrance, Port Douglas"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What's Included (one per line)
                      </label>
                      <textarea
                        value={formData.included_items.join('\n')}
                        onChange={(e) => handleArrayFieldChange('included_items', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="4"
                        placeholder="Professional diving instructor&#10;All scuba diving equipment&#10;Underwater photography"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What to Bring (one per line)
                      </label>
                      <textarea
                        value={formData.what_to_bring.join('\n')}
                        onChange={(e) => handleArrayFieldChange('what_to_bring', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="4"
                        placeholder="Swimwear&#10;Towel&#10;Sun protection"
                      />
                    </div>
                  </div>
                )}

                {/* Media Tab */}
                {activeTab === 'media' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hero Image URL
                      </label>
                      <input
                        type="url"
                        value={formData.hero_image}
                        onChange={(e) => setFormData({...formData, hero_image: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="https://example.com/image.jpg"
                      />
                      {formData.hero_image && (
                        <img 
                          src={formData.hero_image} 
                          alt="Hero preview" 
                          className="mt-2 w-32 h-32 object-cover rounded border"
                          onError={(e) => {
                            e.target.src = '/api/placeholder/128/128';
                          }}
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hero Image Alt Text
                      </label>
                      <input
                        type="text"
                        value={formData.hero_image_alt}
                        onChange={(e) => setFormData({...formData, hero_image_alt: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Divers exploring colorful coral reef"
                      />
                    </div>
                  </div>
                )}

                {/* Pricing Tab */}
                {activeTab === 'pricing' && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Base Price (AUD) *
                        </label>
                        <input
                          type="number"
                          value={formData.base_price}
                          onChange={(e) => setFormData({
                            ...formData, 
                            base_price: parseFloat(e.target.value) || 0,
                            price_display: `From $${e.target.value || '0'}`
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="450"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price Display Text
                        </label>
                        <input
                          type="text"
                          value={formData.price_display}
                          onChange={(e) => setFormData({...formData, price_display: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="From $450"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Advance Booking (days)
                        </label>
                        <input
                          type="number"
                          value={formData.advance_booking_days}
                          onChange={(e) => setFormData({...formData, advance_booking_days: parseInt(e.target.value) || 1})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Currency
                        </label>
                        <select
                          value={formData.currency}
                          onChange={(e) => setFormData({...formData, currency: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="AUD">AUD - Australian Dollar</option>
                          <option value="USD">USD - US Dollar</option>
                          <option value="EUR">EUR - Euro</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cancellation Policy
                      </label>
                      <textarea
                        value={formData.cancellation_policy}
                        onChange={(e) => setFormData({...formData, cancellation_policy: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="4"
                        placeholder="Free cancellation up to 48 hours before the adventure..."
                      />
                    </div>
                  </div>
                )}

                {/* SEO Tab */}
                {activeTab === 'seo' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meta Title
                      </label>
                      <input
                        type="text"
                        value={formData.meta_title}
                        onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Great Barrier Reef Diving Adventure - Dive Camp Adventures"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Recommended: 50-60 characters (Current: {formData.meta_title.length})
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Meta Description
                      </label>
                      <textarea
                        value={formData.meta_description}
                        onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows="3"
                        placeholder="Explore the Great Barrier Reef with professional guides. Book your diving adventure today!"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Recommended: 150-160 characters (Current: {formData.meta_description.length})
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Keywords (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={formData.meta_keywords}
                        onChange={(e) => setFormData({...formData, meta_keywords: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="scuba diving, great barrier reef, diving tours, underwater adventure"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tags (comma-separated)
                      </label>
                      <input
                        type="text"
                        value={formData.tags.join(', ')}
                        onChange={(e) => handleTagsChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="diving, coral reef, marine life, adventure"
                      />
                    </div>
                  </div>
                )}

                {/* Settings */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h4 className="text-lg font-medium text-gray-900 mb-4">Adventure Settings</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_active"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                          Adventure is active (visible on website)
                        </label>
                      </div>
                      
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="is_featured"
                          checked={formData.is_featured}
                          onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="is_featured" className="ml-2 text-sm text-gray-700">
                          Featured adventure (highlighted on homepage)
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="booking_enabled"
                          checked={formData.booking_enabled}
                          onChange={(e) => setFormData({...formData, booking_enabled: e.target.checked})}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="booking_enabled" className="ml-2 text-sm text-gray-700">
                          Booking enabled
                        </label>
                      </div>

                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="requires_certification"
                          checked={formData.requires_certification}
                          onChange={(e) => setFormData({...formData, requires_certification: e.target.checked})}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="requires_certification" className="ml-2 text-sm text-gray-700">
                          Requires diving certification
                        </label>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Minimum Age
                        </label>
                        <input
                          type="number"
                          value={formData.min_age}
                          onChange={(e) => setFormData({...formData, min_age: parseInt(e.target.value) || 12})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Maximum Age (optional)
                        </label>
                        <input
                          type="number"
                          value={formData.max_age || ''}
                          onChange={(e) => setFormData({...formData, max_age: e.target.value ? parseInt(e.target.value) : null})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Sort Order
                        </label>
                        <input
                          type="number"
                          value={formData.sort_order}
                          onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {saving ? 'Saving...' : 'Changes are saved automatically'}
                  </div>
                  <button
                    onClick={saveAdventure}
                    disabled={saving || !formData.title || !formData.slug}
                    className={`px-8 py-3 rounded-lg font-medium transition-colors ${
                      saving || !formData.title || !formData.slug
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    } text-white flex items-center`}
                  >
                    {saving && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    {saving ? 'Saving...' : (isCreating ? 'Create Adventure' : 'Save Changes')}
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <span className="text-6xl mb-6 block">🤿</span>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                Select an Adventure to Edit
              </h3>
              <p className="text-gray-600 mb-6">
                Choose an adventure from the list on the left, or create a new one to get started.
              </p>
              <button
                onClick={createNewAdventure}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create New Adventure
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalAdventuresManager;